import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

interface ConsistenciesManagerFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    schema: any;

    meta: any;
}

const state: ConsistenciesManagerFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    schema: null,

    meta: {},
};

const initState: ConsistenciesManagerFormState = cloneDeep(state);

export const consistencies_MANAGER_DATA = 'GET_consistensies_MANAGER_DATA';
export const consistencies_MANAGER_SCHEMA = 'GET_consistensies_MANAGER_SCHEMA';

export const actions: ActionTree<ConsistenciesManagerFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(consistencies_MANAGER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', result.getOperation(consistencies_MANAGER_DATA).getContent());

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(consistencies_MANAGER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:voc_consistencies`
                    }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(consistencies_MANAGER_SCHEMA).getContent(),
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('consistencies_MANAGER_DATA_SAVE')
                    .update(documentId, normalizedForm)
            } else {
                saveQuery = vm.$q('consistencies_MANAGER_DATA_SAVE')
                    .create('s:voc_consistencies', normalizedForm)
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('consistencies_MANAGER_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

};

export const mutations: MutationTree<ConsistenciesManagerFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_consistencies_MANAGER_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, state.document);
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: ConsistenciesManagerFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },
};

const getters = {
    fields: (state: ConsistenciesManagerFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_consistencies_manager_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
